import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { SelectedClub } from '../components';
import { EventRow } from '../components/Club/EventRow';
import { CloneEvent } from '../components/Modals/CloneEvent';
import { IEvent, getPastClubEvents } from '../db';
import { AppRoutes } from '../router';
import { IRootState } from '../store';
import { setPageTitle } from '../store/themeConfigSlice';

interface IGroupdEvent {
    year: number;
    events: IEvent[];
}

const Events: FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [events, setEvents] = useState<Array<IGroupdEvent>>([]);
    const [eventToClone, setEventToClone] = useState<IEvent | null>(null);
    const {
        club: { selectedClub },
    } = useSelector((state: IRootState) => state);

    const fetchEvents = useCallback(() => {
        if (selectedClub) {
            getPastClubEvents(selectedClub._id, true).then((response) => {
                const groupedEvents = response.reduce((acc: IGroupdEvent[], event: IEvent) => {
                    if (event.dates && event.dates.length) {
                        const year = new Date(event.dates[0]).getFullYear();
                        const existingGroup = acc.find(group => group.year === year);
                        if (existingGroup) {
                            existingGroup.events.push(event);
                        } else {
                            acc.push({ year, events: [event] });
                        }
                    }
                    return acc;
                }, []);
                setEvents(groupedEvents);
            });
        }
    }, [selectedClub]);

    useEffect(() => fetchEvents(), [selectedClub]);

    useEffect(() => {
        dispatch(setPageTitle('Club Past Events'));
    });

    return (
        <section>
            <SelectedClub />
            <div>
                <ul className="flex space-x-2 rtl:space-x-reverse">
                    <li>
                        <Link to={AppRoutes.DASHBOARD} className="text-primary hover:underline">
                            {t('DASHBOARD')}
                        </Link>
                    </li>
                    <li className="before:content-['/'] before:mr-1 rtl:before:ml-1">
                        <span>{t('PAST_EVENTS')}</span>
                    </li>
                </ul>
                <div className="pt-5">
                    <div className="mb-6">
                        {events.length === 0 && <div className="text-center">{t('NO_EVENTS')}</div>}
                        <div className='flex flex-wrap text-xl'>
                            {t("JUMP_YEAR")}:
                            {events.map((event) => event.year).map((year) => (
                                <a key={year} href={`#${year}`} className="ms-2 hover:no-underline underline text-info">
                                    {year}
                                </a>
                            ))}
                        </div>
                        {events?.map((event) => {
                            return <section id={event.year.toString()} key={event.year} className='mb-6'>
                                <h2 className="text-2xl text-gray-700 font-bold mb-2 mt-10">{event.year} ({event.events.length})</h2>
                                <div className='mb-6 flex flex-col gap-4'>
                                    {event.events.sort((a: IEvent, b: IEvent) => new Date(a.dates[0]).getTime() - new Date(b.dates[0]).getTime()).map((event) => (
                                        <EventRow key={event._id} event={event} club={selectedClub} fetchEvents={fetchEvents} handleCloneEvent={() => setEventToClone(event)} />
                                    ))}
                                </div>
                            </section>
                        })}
                    </div>
                </div>
            </div>
            <CloneEvent eventToClone={eventToClone} setEventToClone={setEventToClone} fetchEvents={fetchEvents} />
        </section>
    );
};

export default Events;
